import { gql } from '@parkchirp/api-client';
import moment from 'moment-timezone';

import { getFieldListString, getInvoiceQueryFields, getInvoicesQueryOptions } from '../utils';

const queryListInvoices = (variables, fields) => {
	const query = `
    query listInvoices(
      $limit: Int,
      $offset: Int,
      $filters: [Filter],
      $filter_expressions: [FilterExpression],
      $order: Order,
      $orders: [Order],
      $scope_to_user: Boolean
    ) {
      listInvoices(
        limit: $limit,
        offset: $offset,
        filters: $filters,
        filter_expressions: $filter_expressions,
        order: $order,
        orders: $orders
        scope_to_user: $scope_to_user
      ) {
        ${getFieldListString(fields)}
      }
    }
  `;

	return { query, variables };
};

export const DEFAULT_TIMEZONE = 'America/Chicago';
/**
 * Use this for timestamps sent to GraphQL
 * Converts midnight in the given timezone to UTC
 * @param   {Date}      date
 * @param   {String}    timezone
 * @returns {String}    ISO String (e.g. 2022-09-19T05:00:00Z)
 */
export const convertDateToUTC = (date, timezone) => {
	const tz = timezone ?? DEFAULT_TIMEZONE;
	const dateNow = new Date();

	const dateOnly = dateNow.toISOString().slice(0, 10);
	const dateInTimezone = moment.tz(`${dateOnly} 00:00:00`, 'YYYY-MM-DD HH:mm:ss', tz);

	return dateInTimezone.tz('UTC').format().replace('T', ' ').replace('Z', ' ');
};

export const getInvoicesQuery = ({ facilityId = null }) => {
	const options = getInvoicesQueryOptions({ facilityId });
	const invoiceQueryFields = getInvoiceQueryFields({
		subscriptionNameFlag: true,
		accountIdFlag: true,
	});
	const newOptions = { ...options, scope_to_user: false };
	const { query, variables } = queryListInvoices(newOptions, invoiceQueryFields);

	return { GET_INVOICES: gql`${query}`, variables };
};

export const ListBatchPayments = gql`
    query listBatchPayments($organization_id: String!) {
        listBatchPayments(orgId: $organization_id) { 
            batch_payment { 
                id,
                createdAt,
                report_id,
                status,
                amount,
                type,
                facilities,
                posted_at,
                updated_at,
            }
        }
    }
`;

export const LIST_PAYMENT_GROUPS_V2 = gql`
    query listPaymentGroups(
        $limit: Int
        $offset: Int
        $order: SortOrder
        $filters: [Filter]
    ) {
        listPaymentGroups(
            limit: $limit
            offset: $offset
            order: $order
            filters: $filters
        ) {
            id
            paymentMetaId
            paymentType
            amount
        }
    }
`;

export const LIST_PAYMENT_GROUPS_COUNT_V2 = gql`
    query listPaymentGroupsCount(
        $filters: [Filter]
    ) {
        listPaymentGroupsCount(
            filters: $filters
        ) {
            total
        }
    }
`;

export const LIST_PAYMENTS_FROM_CACHE = gql`
    query listPaymentsFromCache(
        $limit: Int
        $offset: Int
        $order: SortOrder
        $filters: [Filter]
    ) {
        listPaymentsFromCache(
            limit: $limit
            offset: $offset
            order: $order
            filters: $filters
        ) {
            count
            payments {
                id
                status
                amount
                facilityId
                creditEntryApplicationType
                facilityNumber
                type
                invoiceReadableId
                organizationId
                paymentGroupId
                accountId
                createdAt
                subscriptionName
                paymentMetaId
                invoiceId
                facilityName
                declineDate
                isUpdating
                declineReason
                isReversed
                facilityTimezone
                paymentBatchId
                subscriptionId
                applicationDate
                last4
            }
            orgTimezone
        }
    }
`;

export const GET_ORGANIZATION_SETTINGS_BY_USER: any = gql`
    query getOrganizationSettingsByUser{
        getOrganizationSettingsByUser {
            timezone,
            require_payment_batch_approval
        }
    }
`;

export const GET_ACH_REJECTIONS_FROM_CACHE_CSV = gql`
    query getACHRejectionsFromCacheCSV(
        $limit: Int!
        $offset: Int!
        $order: SortOrder
        $filters: [Filter]
    ) {
        getACHRejectionsFromCacheCSV(
            limit: $limit
            offset: $offset
            order: $order
            filters: $filters
        ) {
            url
        }
    }
`;

export const GET_PAYMENTS_CSV = gql`
    query getPaymentsCSV(
        $limit: Int!
        $offset: Int!
        $order: SortOrder
        $filters: [Filter]
    ) {
        getPaymentsCSV(
            limit: $limit
            offset: $offset
            order: $order
            filters: $filters
        ) {
            url
        }
    }
`;

export const GET_BULK_PAYMENT_UPLOAD = gql`
    query getBulkPaymentUpload(
        $fileId: Int!
    ) {
        getBulkPaymentUpload(
            fileId: $fileId
        ) {
            paymentType
            paymentAmount
            applicationDate
            paymentMetaId
            rows {
                accountId
                invoiceNumber
                appAmount
                accountIdError
                invoiceNumberError
                appAmountError
                applicationDateError
                invalidFacilityPaymentTypeError
            }
        }
    }
`;

export const LIST_CHARGEBACKS = gql`
    query listChargebacks(
        $limit: Int
        $offset: Int
        $filters: [Filter]
        $order: [SortOrder]
    ) {
        listChargebacks(
            limit: $limit
            offset: $offset
            filters: $filters
            order: $order
        ) {
            total
            chargebacks {
               id
               gpiDisputeId
               amount
               result
               stage
               status
               respondBy
               reasonCode
               reasonDescription
               createdAt
               gpiCreatedAt
               cardBrand
               cardLast4
               globalPayApiCredentialId
               payment {
                    id
                    paymentMetaId
                    subscription {
                        subscriptionName
                        accountId
                    }
                }
                facility {
                    id
                    facilityName
                    facilityNumber
                }
            }
        }
    }
`;

export const GET_CHARGEBACKS_CSV = gql`
    query getChargebacksCsv(
        $limit: Int
        $offset: Int
        $filters: [Filter]
        $order: [SortOrder]
    ) {
        getChargebacksCsv(
            limit: $limit
            offset: $offset
            filters: $filters
            order: $order
        ) {
            url
        }
    }
`;

export const SYNC_AND_GET_CHARGEBACK = gql`
    query syncAndGetChargeback(
        $chargebackId: Int!
        $globalPayApiCredentialId: Int!
        ) {
        syncAndGetChargeback(
            chargebackId: $chargebackId
            globalPayApiCredentialId: $globalPayApiCredentialId
        ) {
            id
            gpiDisputeId
            globalPayApiCredentialId
            stage
            status
            respondBy
            reasonCode
            reasonDescription
            amount
            result
            organizationId
            payment {
                id
                paymentMetaId
            }
            logs {
                id
                action
                updateSource
                createdAt
                user {
                    userId
                    firstName
                    lastName
                    email
                }
            }
            files {
                id
                fileId
                file {
                    fileName
                    bucketPath
                }
            }
        }
    }
`;
